import React, { useState, useEffect } from 'react';
import './CareerForm.css';

import { Heading, PageTitle } from '../../../components';

import { FaUser, FaEnvelope, FaBriefcase, FaCommentDots, FaAngleDown, FaCertificate, FaPhoneAlt } from 'react-icons/fa';
import { FaLocationDot } from 'react-icons/fa6';

import axios from "axios"; // Axios for HTTP requests

// API endpoint for form submission
const API_PATH = 'https://siraj-react.asdesignsgalaxy.com/api/phpmailer/career.php';

// Regular expressions for validation
const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;

const CareerForm = () => {

  // State for form data with initial empty values
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    number: '',
    position: '',
    location: '',
    skills: '',
    experience: '',
    portfolio: '',
    message: ''
  });

  // State for tracking form submission status
  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Effect to automatically hide success message after 3 seconds
  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  /**
   * Handles form submission
   * @param {Event} e - The form submission event
   */
  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      // Validate form fields
      const validationErrors = [];

      if (!formData.name.trim()) validationErrors.push('Full name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.number.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.number)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.position) validationErrors.push('Position is required');
      if (!formData.location.trim()) validationErrors.push('Location is required');
      if (!formData.skills.trim()) validationErrors.push('Skills are required');
      if (!formData.experience || formData.experience < 0) validationErrors.push('Valid years of experience is required');
      if (!formData.message.trim()) validationErrors.push('Cover letter is required');

      // If validation errors exist, throw them
      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      // Prepare form data for submission
      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      // Submit form data to the API
      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      // Handle API response
      if (!response.data.sent) {
        throw new Error(response.data.message || 'Application submission failed');
      }

      // On success: show success message and reset form
      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        number: '',
        position: '',
        location: '',
        skills: '',
        experience: '',
        portfolio: '',
        message: ''
      });
    } catch (err) {
      // Handle errors from API or validation
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      // Reset submission state
      setIsSubmitting(false);
    }
  };

  /**
   * Handles input changes and updates form state
   * @param {Event} e - The input change event
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <>
      <PageTitle title={'Join Our Islamic Center Team'} page={'Career Form'} />

      <section className="career-form">
        <div className="box-container">
          <div className="content">
            <form className="dark-form" onSubmit={handleSubmitForm}>

              {/* Form heading */}
              <Heading 
                title={"Islamic Center Career Opportunities"} 
                subTitle={"Become Part of Our Team"} 
                linear 
                sideHeading 
              />

              {/* Name and Email fields */}
              <div className="input-box">
                <div className="input-field">
                  <label htmlFor="name"><FaUser /></label>
                  <input
                    type="text"
                    name="name"
                    className="box"
                    id="name"
                    placeholder="Your Full Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className="input-field">
                  <label htmlFor="email"><FaEnvelope /></label>
                  <input
                    type="email"
                    name="email"
                    className="box"
                    id="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Phone and Position fields */}
              <div className="input-box">
                <div className="input-field">
                  <label htmlFor="number"><FaPhoneAlt /></label>
                  <input
                    type="number"
                    name="number"
                    className="box"
                    id="number"
                    placeholder="Your Phone Number"
                    value={formData.number}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className="select">
                  <div className="select-container">
                    <label htmlFor="position"><FaBriefcase /></label>
                    <select
                      className="select-box"
                      id="position"
                      name="position"
                      value={formData.position}
                      onChange={handleInputChange}
                      required
                      disabled={isSubmitting}
                    >
                      <option value="">Select Position</option>
                      <option value="Imam">Imam</option>
                      <option value="Quran Teacher">Quran Teacher</option>
                      <option value="Islamic Studies Instructor">Islamic Studies Instructor</option>
                      <option value="Youth Coordinator">Youth Coordinator</option>
                      <option value="Administrator">Administrator</option>
                      <option value="Event Coordinator">Event Coordinator</option>
                      <option value="Other">Other</option>
                    </select>
                    <div className="icon-container"><FaAngleDown className="icon" /></div>
                  </div>
                </div>
              </div>

              {/* Location field */}
              <div className="input-box">
                <div className="input-field">
                  <label htmlFor="location"><FaLocationDot /></label>
                  <input
                    type="text"
                    name="location"
                    className="box"
                    id="location"
                    placeholder="Your City/State"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Skills and Experience fields */}
              <div className="input-box">
                <div className="input-field">
                  <label htmlFor="skills"><FaCertificate /></label>
                  <input
                    type="text"
                    name="skills"
                    className="box"
                    id="skills"
                    placeholder="Relevant Skills (e.g., Quran Recitation, Teaching)"
                    value={formData.skills}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  />
                </div>

                <div className="input-field">
                  <label htmlFor="experience"><FaBriefcase /></label>
                  <input
                    type="number"
                    name="experience"
                    className="box"
                    id="experience"
                    placeholder="Years of Relevant Experience"
                    value={formData.experience}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                    min="0"
                  />
                </div>
              </div>

              {/* Portfolio/CV link field */}
              <div className='input-box'>
                <div className="input-field">
                  <label htmlFor="portfolio"><FaBriefcase /></label>
                  <input
                    type="url"
                    name="portfolio"
                    className="box"
                    id="portfolio"
                    placeholder="Portfolio/CV Link (if available)"
                    value={formData.portfolio}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  />
                </div>
              </div>

              {/* Message/cover letter textarea */}
              <div className="input-field text-area">
                <label htmlFor="message"><FaCommentDots /></label>
                <textarea
                  cols="30"
                  rows="6"
                  name="message"
                  className="box"
                  id="message"
                  placeholder="Tell us about your Islamic knowledge, experience, and why you'd be a great fit for our center"
                  value={formData.message}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                ></textarea>
              </div>

              {/* Submit button */}
              <button 
                type="submit" 
                className="btn" 
                disabled={isSubmitting}
                style={{background: 'linear-gradient(144deg, #1a5e1a, #2e8b57, #1a5e1a)'}}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Application'}
              </button>

              {/* Error messages display */}
              {error && (
                <div className="alert error">
                  {error.split(' | ').map((msg, i) => (
                    <span key={i}>{msg}</span>
                  ))}
                </div>
              )}

              {/* Success message display */}
              {mailSent && (
                <div className="alert success">
                  <span>Application Submitted Successfully! JazakAllah Khair</span>
                </div>
              )}
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

export default CareerForm;